import { useFormik } from 'formik'
import { useState } from 'react'
import { NumericFormat } from 'react-number-format'
import ModalComponent from '..'
import {
  deepCopy,
  formatCurrency,
  formatDate,
  formatDateTime,
  getCurrencySymbol,
  parseCurrencyToNumber,
  parseDateStringToDate,
  removeElementAtIndex,
} from '../../../helpers'
import { useFile } from '../../../hooks/useFile'
import { useModalControl } from '../../../hooks/useModalControl'
import {
  useCreateEventMutation,
  useEditEventMutation,
} from '../../../redux-toolkits/events/events.slice'
import { useGetFulfilmentCentersQuery } from '../../../redux-toolkits/vendors/vendors.slice'
import {
  EventTicketTier,
  SingleEvent,
} from '../../../redux-toolkits/events/events.type'
import {
  addEventocationSchema,
  CreateEventSchema,
} from '../../../validation/validate'
import { Button } from '../../button'
import { ImageUploadButton } from '../../button/imageUploadButton'
import { ActionChip } from '../../cards/actionChip'
import { CategoryCard } from '../../cards/categoryCard'
import { EmptyState } from '../../emptyState/emptyState'
import Icon from '../../icon'
import { ImageBanner } from '../../images/imageBanner'
import { Checkbox } from '../../inputs/Checkbox'
import { DateInput } from '../../inputs/dateInput'
import Dropdown from '../../inputs/dropdown'
import { EditInput } from '../../inputs/editInput'
import { TextInput } from '../../inputs/textInput'
import { TimeInput } from '../../inputs/timeInput'
import { TabNav } from '../../nav/tabNav'
import { ErrorText } from '../../typography/ErrorText'
import { AddLocationModal } from './addLocationModal'
import { EventFormValues, toEventPayload } from './event-form-payload'
import {
  AROUND_LAGOS_LABEL,
  applyVenueCentre,
  getVenueCentreLabel,
  getVenueCentres,
} from './event-venue-centre'
import { AddTicketTierModal } from './addTicketTierModal'
import { ImageUploadPreviewModal } from './ImagePreviewModal'
import { SelectCategoriesModal } from './selectCategoriesModal'
import { SelectCreatorModal } from './selectCreatorModal'
import { SelectTagsModal } from './selectTagsModal'

export const CreateEventModal = ({
  modalOpen,
  handleModalClose,
  setModalOpen,
  mode,
  event,
  onSucess,
}: {
  modalOpen: boolean
  handleModalClose: () => void
  setModalOpen: (open: boolean) => void
  mode: 'create' | 'edit'
  event?: SingleEvent
  onSucess: () => void
}) => {
  const [selectedTier, setSelectedTier] = useState<{
    idx: number
    tier: EventTicketTier
  }>()

  const modalControls = useModalControl()

  const { handleFileChange, uploadedImage, previewModalOpen, reset } = useFile({
    modalControls: modalControls,
  })

  const [createEvent, { isLoading: creatingEvent }] = useCreateEventMutation()

  const [editEvent, { isLoading: editingEvent }] = useEditEventMutation()

  const { data: fulfilmentCentresResponse } = useGetFulfilmentCentersQuery(
    { limit: 200 },
    { skip: !modalOpen },
  )
  const venueCentres = getVenueCentres(fulfilmentCentresResponse?.data?.docs)

  const {
    values,
    handleBlur,
    handleChange,
    handleSubmit,
    errors,
    touched,
    setFieldValue,
    resetForm,
  } = useFormik({
    initialValues: {
      name: event?.name ?? '',
      description: event?.description ?? '',
      categoryId: event?.category?.map((c) => c._id) ?? [],
      images: event?.images ?? [],
      vendorId: event?.vendor?._id ?? '',
      tags: event?.tags?.map((i) => i._id) ?? [],
      trackQuantity: event?.trackQuantity ?? true,
      price: event?.price ?? '',
      isMarkupEnabled: event?.isMarkupEnabled ?? false,
      markupValue: event?.markupValue ?? 0,
      inStockQuantity: event?.inStockQuantity ?? '',
      eventTicketSettings: event?.eventTicketSettings
        ? {
            ...event.eventTicketSettings,
            eventDate: formatDate(
              event.eventTicketSettings?.eventDate,
              'YYYY-MM-DD HH:mm:ss.SSS',
              { mode: 'fromUtc' },
            ),
          }
        : {
            venueCentre: null,
            venue: '',
            address: '',
            city: '',
            eventDate: '',
            minQuantityPerPurchase: undefined,
            maxQuantityPerPurchase: undefined,
            tiers: [],
          },
      isActive: event?.isActive ?? false,
      isTiered: (event?.eventTicketSettings?.tiers?.length ?? 0) > 0,
      categories: event?.category ?? [],
      vendor: event?.vendor
        ? {
            _id: event.vendor._id,
            vendorId: event.vendor._id,
            slug: '',
            name: event.vendor.businessInformation?.businessName ?? '',
            logo: '',
            banner: '',
            extendedBio: '',
          }
        : undefined,
      tags_: event?.tags ?? [],
    } as EventFormValues,
    enableReinitialize: true,
    validationSchema: CreateEventSchema,
    onSubmit: async (vals) => {
      const payload = toEventPayload(vals)

      if (mode === 'create') {
        const resp = await createEvent(payload)

        if (resp.data?.success) {
          resetForm()
          onSucess()
        }
      } else {
        const resp = await editEvent({
          id: event?._id ?? '',
          payload: payload,
        })

        if (resp.data?.success) {
          resetForm()
          onSucess()
        }
      }
    },
  })

  return (
    <>
      <ModalComponent
        open={modalOpen}
        setOpen={setModalOpen}
        onCloseCallback={() => {
          handleModalClose()
          resetForm()
        }}
        title={mode === 'edit' ? `Edit Event` : `Create Event`}
        position="right"
      >
        <form onSubmit={handleSubmit} className="w-full flex flex-col gap-6">
          <div className="flex items-center justify-between gap-4">
            <TabNav
              activeTab={values.isTiered ? 'tiered' : 'simple'}
              setActiveTab={(t) => {
                setFieldValue('isTiered', t === 'tiered')
              }}
              tabs={[
                {
                  name: 'Simple Event',
                  id: 'simple',
                },
                {
                  name: 'Tiered Event',
                  id: 'tiered',
                },
              ]}
              type="switcher-black"
              tabClassName="!py-3 !px-6"
              className="!w-fit"
            />

            {mode === 'edit' ? (
              <Dropdown
                menuButton={
                  <Button
                    icon="ellipses"
                    className="size-[48px] !px-0"
                    type="button"
                  />
                }
                menuItems={[
                  {
                    name: `${values.isActive ? 'Deactivate' : 'Activate'}`,
                    value: '',
                    onClick: () => {
                      setFieldValue('isActive', !values.isActive)
                      handleSubmit()
                    },
                  },
                ]}
                menuItemClassName="border-b-0"
                menuClassName="!w-[200px]"
                loading={editingEvent}
                closeOnClick={false}
              />
            ) : undefined}
          </div>

          {/* Images  */}
          <div className="mt-4 overflow-x-scroll w-full">
            <div className="flex flex-row gap-6 w-fit">
              {values.images.map((imageUrl, idx) => {
                const key = `productImage-${idx}`
                return (
                  <ImageBanner
                    key={key}
                    className="!h-[140px] !w-[140px]"
                    imageUrl={imageUrl}
                    onDelete={() => {
                      const imageList = deepCopy(values.images ?? [])

                      setFieldValue(
                        'images',
                        removeElementAtIndex(imageList, idx),
                      )
                    }}
                  />
                )
              })}
              {values.images.length < 3 && (
                <ImageUploadButton
                  showText
                  handleChange={handleFileChange}
                  className="!relative !rounded-lg size-[140px] !m-0 !top-0 !right-0"
                  icon="pic-icon"
                />
              )}
            </div>

            <ErrorText text={errors.images ? (errors.images as string) : ''} />
          </div>

          <TextInput
            type="text"
            name="name"
            title="Event name"
            placeholder="Event name"
            value={values.name}
            onBlur={handleBlur}
            onChange={handleChange}
            errors={errors.name && touched.name ? errors.name : ''}
            hasIcon={false}
            readOnly={false}
            showAsReadOnly
            className=""
          />

          <TextInput
            type="textarea"
            name="description"
            title="Event description"
            placeholder="Event description"
            value={values.description}
            onBlur={handleBlur}
            onChange={handleChange}
            errors={
              errors.description && touched.description
                ? errors.description
                : ''
            }
            hasIcon={false}
            readOnly={false}
            showAsReadOnly
            className=""
          />

          <div className="grid grid-cols-2 gap-4">
            <DateInput
              name="eventTicketSettings.eventDate"
              onBlur={handleBlur}
              placeholder="Event date"
              errors={
                errors.eventTicketSettings?.eventDate &&
                touched.eventTicketSettings?.eventDate
                  ? errors.eventTicketSettings?.eventDate
                  : ''
              }
              title="Event Date"
              date={parseDateStringToDate(
                values.eventTicketSettings?.eventDate,
                'YYYY-MM-DD HH:mm:ss.SSS',
                true,
              )}
              setDate={(date) => {
                setFieldValue(
                  'eventTicketSettings.eventDate',
                  formatDate(date, 'YYYY-MM-DD HH:mm:ss.SSS'),
                )
              }}
              dateFormat="MMM DD, YYYY"
            />

            <TimeInput
              name={'time'}
              date={parseDateStringToDate(
                values.eventTicketSettings.eventDate,
                'YYYY-MM-DD HH:mm:ss.SSS',
                true,
              )}
              title="Event time"
              placeholder="Event time"
              setDate={(val) => {
                if (values.eventTicketSettings.eventDate) {
                  setFieldValue(
                    'eventTicketSettings.eventDate',
                    formatDateTime(
                      values.eventTicketSettings.eventDate,
                      formatDate(val, 'hh:mm A'),
                      {
                        dateFormat: 'YYYY-MM-DD HH:mm:ss.SSS',
                        timeFormat: 'hh:mm A',
                        resultFormat: 'YYYY-MM-DD HH:mm:ss.SSS',
                      },
                    ),
                  )
                }
              }}
              onBlur={handleBlur}
              errors={
                errors.eventTicketSettings?.eventDate &&
                touched.eventTicketSettings?.eventDate
                  ? errors.eventTicketSettings?.eventDate
                  : ''
              }
              diasbled={!values.eventTicketSettings.eventDate}
            />
          </div>

          {values.isTiered ? undefined : (
            <>
              <NumericFormat
                suffix={undefined}
                prefix={getCurrencySymbol('NGN')}
                decimalScale={2}
                thousandSeparator
                customInput={TextInput}
                type="text"
                name="price"
                title={`Price`}
                placeholder="Price"
                value={values.price}
                onBlur={handleBlur}
                onChange={handleChange}
                errors={errors?.price && touched?.price ? errors?.price : ''}
                hasIcon={false}
                readOnly={false}
                showAsReadOnly
                className=""
                disabled={false}
              />

              <Checkbox
                checkStatus={values.isMarkupEnabled ?? false}
                text="Enable markup percentage"
                handleChange={(enabled) => {
                  setFieldValue('isMarkupEnabled', enabled)
                }}
              />

              {values.isMarkupEnabled && (
                <>
                  <TextInput
                    type="number"
                    name="markupValue"
                    title="Markup (%)"
                    placeholder="Enter markup percentage"
                    min={0}
                    step="any"
                    value={String(values.markupValue ?? '')}
                    onBlur={handleBlur}
                    onChange={handleChange}
                    errors={
                      errors.markupValue && touched.markupValue
                        ? errors.markupValue
                        : ''
                    }
                    hasIcon={false}
                  />
                  <p className="text-sm text-sec-black">
                    Customer price:{' '}
                    {formatCurrency(
                      parseCurrencyToNumber(values.price) *
                        (1 + Number(values.markupValue) / 100),
                      { currency: 'NGN', decimalPlaces: 2 },
                    )}
                  </p>
                </>
              )}

              <NumericFormat
                suffix={undefined}
                prefix={undefined}
                decimalScale={0}
                thousandSeparator
                customInput={TextInput}
                type="text"
                name="inStockQuantity"
                title={`Quantity`}
                placeholder="Quantity"
                value={values.inStockQuantity}
                onBlur={handleBlur}
                onChange={handleChange}
                errors={
                  errors?.inStockQuantity && touched?.inStockQuantity
                    ? errors?.inStockQuantity
                    : ''
                }
                hasIcon={false}
                readOnly={false}
                showAsReadOnly
                className=""
                disabled={false}
              />
            </>
          )}

          <NumericFormat
            suffix={undefined}
            prefix={undefined}
            decimalScale={0}
            thousandSeparator
            customInput={TextInput}
            type="text"
            name="eventTicketSettings.minQuantityPerPurchase"
            title={`Minimum quantity per purchase`}
            placeholder="Minimum quantity per purchase"
            value={values.eventTicketSettings.minQuantityPerPurchase ?? ''}
            onBlur={handleBlur}
            onChange={handleChange}
            errors={
              errors?.eventTicketSettings?.minQuantityPerPurchase &&
              touched?.eventTicketSettings?.minQuantityPerPurchase
                ? errors?.eventTicketSettings?.minQuantityPerPurchase
                : ''
            }
            hasIcon={false}
            readOnly={false}
            showAsReadOnly
            className=""
            disabled={false}
          />

          <NumericFormat
            suffix={undefined}
            prefix={undefined}
            decimalScale={0}
            thousandSeparator
            customInput={TextInput}
            type="text"
            name="eventTicketSettings.maxQuantityPerPurchase"
            title={`Maximum quantity per purchase`}
            placeholder="Maximum quantity per purchase"
            value={values.eventTicketSettings.maxQuantityPerPurchase ?? ''}
            onBlur={handleBlur}
            onChange={handleChange}
            errors={
              errors?.eventTicketSettings?.maxQuantityPerPurchase &&
              touched?.eventTicketSettings?.maxQuantityPerPurchase
                ? errors?.eventTicketSettings?.maxQuantityPerPurchase
                : ''
            }
            hasIcon={false}
            readOnly={false}
            showAsReadOnly
            className=""
            disabled={false}
          />

          <EditInput
            title="Creator"
            value={values?.vendor ? `${values?.vendor?.name}` : 'Not selected'}
            onEditClick={() => {
              modalControls.handleModalOpen('selectCreatorModal')
            }}
            errors={errors.vendorId && touched.vendorId ? errors.vendorId : ''}
            onCancel={() => {}}
            onEditSave={() => {}}
            editing={false}
            className={''}
          />

          <EditInput
            title="Categories"
            value={undefined}
            onEditClick={() => {
              modalControls.handleModalOpen('selectCategoriesModal')
            }}
            errors={undefined}
            onCancel={() => {}}
            onEditSave={() => {}}
            editing={false}
            className={''}
          >
            {() => {
              return (values?.categories?.length ?? 0) > 0 ? (
                <div className="flex flex-row gap-4 items-center flex-wrap">
                  {values.categories.map((category, idx) => {
                    const key = `category-${idx}`
                    return <CategoryCard category={category} key={key} />
                  })}
                </div>
              ) : (
                'No categories selected'
              )
            }}
          </EditInput>

          <EditInput
            title="Tags"
            value={undefined}
            onEditClick={() => {
              modalControls.handleModalOpen('selectTagsModal')
            }}
            errors={undefined}
            onCancel={() => {}}
            onEditSave={() => {}}
            editing={false}
            className={''}
          >
            {() => {
              return (values?.tags_?.length ?? 0) > 0 ? (
                <div className="flex flex-row gap-4 items-center flex-wrap">
                  {values.tags_.map((tag, idx) => {
                    const key = `tag-${idx}`
                    return (
                      <ActionChip key={key} name={tag.name} icon={tag.icon} />
                    )
                  })}
                </div>
              ) : (
                'No tags selected'
              )
            }}
          </EditInput>

          <Dropdown
            className="w-full"
            menuClassName="!max-h-[240px]"
            position="bottom start"
            menuButton={
              <TextInput
                type="text"
                title="Venue"
                name="venueCentre"
                placeholder="Select venue"
                value={getVenueCentreLabel(
                  values.eventTicketSettings?.venueCentre,
                  venueCentres,
                )}
                onBlur={handleBlur}
                onChange={handleChange}
                hasIcon={false}
                readOnly
                extra={
                  <Icon
                    id={'left-caret'}
                    width={16}
                    height={16}
                    className="-rotate-90 text-sec-black"
                  />
                }
              />
            }
            menuItems={[undefined, ...venueCentres].map((centre) => ({
              name: centre?.name ?? AROUND_LAGOS_LABEL,
              value: centre?._id ?? '',
              onClick: () => {
                setFieldValue(
                  'eventTicketSettings',
                  applyVenueCentre(values.eventTicketSettings, centre),
                )
              },
            }))}
          />

          <EditInput
            title="Event Location"
            value={
              values?.eventTicketSettings?.address
                ? `${values.eventTicketSettings.venue} • ${values.eventTicketSettings.address} • ${values.eventTicketSettings.city}`
                : 'Not selected'
            }
            onEditClick={() => {
              modalControls.handleModalOpen('selectAddressModal')
            }}
            errors={
              errors.eventTicketSettings?.address &&
              touched.eventTicketSettings?.address
                ? errors.eventTicketSettings?.address
                : ''
            }
            onCancel={() => {}}
            onEditSave={() => {}}
            editing={false}
            className={''}
          />

          <Checkbox
            checkStatus={values.trackQuantity}
            handleChange={(s) => {
              setFieldValue('trackQuantity', s)
            }}
            text="Track Quantity"
          />

          {values.isTiered ? (
            <>
              <hr />

              <div className="flex flex-col gap-4">
                <p className="font-medium text-lg">Tiers</p>

                {(values.eventTicketSettings.tiers?.length ?? 0) > 0 ? (
                  values.eventTicketSettings.tiers.map((t, idx) => {
                    const markupValue = t.markupValue ?? event?.markupValue ?? 0
                    const customerPrice =
                      t.price + (t.price * markupValue) / 100
                    return (
                      <div
                        key={`${t.name}-${idx}`}
                        className="flex gap-6 items-start border-b pb-4"
                      >
                        <div className="flex-1 min-w-0">
                          <EditInput
                            title={t.name}
                            value={`${formatCurrency(t.price, {
                              currency: 'NGN',
                              decimalPlaces: 2,
                            })} • ${t.quantity} in stock`}
                            onEditClick={() => {
                              modalControls.handleModalOpen('addTierModal')
                              setSelectedTier({
                                idx: idx,
                                tier: t,
                              })
                            }}
                            errors={undefined}
                            onCancel={() => {}}
                            onEditSave={() => {}}
                            editing={false}
                            className={''}
                          />
                          <p className="text-sm text-sec-black mt-2">
                            {markupValue}% markup • Customer price:{' '}
                            {formatCurrency(customerPrice, {
                              currency: 'NGN',
                              decimalPlaces: 2,
                            })}
                          </p>
                          <div className="mt-3">
                            <Checkbox
                              checkStatus={t.isActive !== false}
                              handleChange={(checked) => {
                                void setFieldValue(
                                  `eventTicketSettings.tiers.${idx}.isActive`,
                                  checked,
                                )
                              }}
                              text={`Show ${t.name} to customers`}
                            />
                            {t.isActive === false && (
                              <p className="text-sm text-sec-black mt-2">
                                Hidden — unavailable for purchase
                              </p>
                            )}
                          </div>
                          {t.description && (
                            <p className="text-sm text-sec-black mt-2 whitespace-pre-wrap break-words">
                              {t.description}
                            </p>
                          )}
                        </div>

                        <button
                          className="max-w-fit flex gap-2 items-center text-danger"
                          type="button"
                          onClick={() => {
                            setFieldValue(
                              'eventTicketSettings.tiers',
                              removeElementAtIndex(
                                deepCopy(values.eventTicketSettings.tiers),
                                idx,
                              ),
                            )
                          }}
                        >
                          Remove
                          <Icon id="bin" width={16} height={16} />
                        </button>
                      </div>
                    )
                  })
                ) : (
                  <EmptyState text="No tiers added" />
                )}

                <Button
                  type="button"
                  label="Add tier"
                  primary={false}
                  icon="plus"
                  onClick={() => {
                    modalControls.handleModalOpen('addTierModal')
                    setSelectedTier(undefined)
                  }}
                  className="!max-w-fit ml-auto"
                />
              </div>
            </>
          ) : undefined}

          <Button
            type="submit"
            label={mode === 'create' ? 'Create Event' : 'Save Changes'}
            loading={creatingEvent || editingEvent}
            disabled={creatingEvent || editingEvent}
            primary
            className="mt-6"
          />
        </form>
      </ModalComponent>

      {uploadedImage && (
        <ImageUploadPreviewModal
          image={uploadedImage}
          open={previewModalOpen}
          setOpen={modalControls.setModalOpen}
          onCloseCallback={() => {
            reset()
          }}
          onSuccessCallback={(url) => {
            const imagesCopy = deepCopy(values.images ?? [])
            imagesCopy.push(url)
            setFieldValue('images', imagesCopy)
            reset()
          }}
        />
      )}

      <SelectCreatorModal
        open={
          modalControls.modalOpen &&
          modalControls.modal === 'selectCreatorModal'
        }
        setOpen={modalControls.setModalOpen}
        onCloseCallback={() => {
          modalControls.handleModalClose()
        }}
        onSuccessCallback={(selected) => {
          setFieldValue('vendorId', selected[0]._id)
          setFieldValue('vendor', selected[0])
          modalControls.handleModalClose()
        }}
        selectedCreators={values.vendor ? [values.vendor] : []}
        maxSelection={1}
        loading={false}
      />

      <SelectCategoriesModal
        open={
          modalControls.modalOpen &&
          modalControls.modal === 'selectCategoriesModal'
        }
        setOpen={modalControls.setModalOpen}
        onCloseCallback={() => {
          modalControls.handleModalClose()
        }}
        onSuccessCallback={(c) => {
          setFieldValue('categories', c)
          setFieldValue(
            'categoryId',
            c.map((i) => i._id),
          )
          modalControls.handleModalClose()
        }}
        selectedCategories={values.categories}
        maxSelection={undefined}
        loading={false}
      />

      <SelectTagsModal
        open={
          modalControls.modalOpen && modalControls.modal === 'selectTagsModal'
        }
        setOpen={modalControls.setModalOpen}
        onCloseCallback={() => {
          modalControls.handleModalClose()
        }}
        onSuccessCallback={(c) => {
          setFieldValue('tags_', c)
          setFieldValue(
            'tags',
            c.map((i) => i._id),
          )
          modalControls.handleModalClose()
        }}
        selectedTags={values.tags_}
      />

      <AddLocationModal
        open={
          modalControls.modalOpen &&
          modalControls.modal === 'selectAddressModal'
        }
        setOpen={modalControls.setModalOpen}
        onCloseCallback={() => {
          modalControls.handleModalClose()
        }}
        onSuccessCallback={(loc, reset) => {
          setFieldValue('eventTicketSettings', {
            ...values.eventTicketSettings,
            venue: loc.name,
            city: loc.city,
            address: loc.address,
          })
          reset()
          modalControls.handleModalClose()
        }}
        fields={['address', 'name', 'city']}
        schema={addEventocationSchema}
        loading={false}
        title="Event Location"
      />

      <AddTicketTierModal
        open={modalControls.modalOpen && modalControls.modal === 'addTierModal'}
        setOpen={modalControls.setModalOpen}
        onCloseCallback={() => {
          modalControls.handleModalClose()
          setSelectedTier(undefined)
        }}
        onSuccessCallback={(tier) => {
          const tierCopy = deepCopy(values.eventTicketSettings.tiers) ?? []

          if (selectedTier?.idx !== undefined) {
            tierCopy.splice(selectedTier.idx, 1, tier)
          }

          setFieldValue('eventTicketSettings', {
            ...values.eventTicketSettings,
            tiers:
              selectedTier?.idx === undefined ? [...tierCopy, tier] : tierCopy,
          })
          modalControls.handleModalClose()
          setSelectedTier(undefined)
        }}
        eventId={mode === 'edit' ? event?._id : undefined}
        ticket={selectedTier?.tier}
        defaultMarkupValue={event?.markupValue}
      />
    </>
  )
}
